import { useEffect, useRef } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { useAppStore } from "../store/appStore";
import appStyles from "../styles/styles";

/**
 * リアルタイム文字起こしの表示エリア。
 *
 * 自動スクロール仕様（コミット b300175 / 685ecc8 の挙動を維持）:
 * - 新しいテキストが追加されたら最下部へ自動スクロールする
 * - ユーザーが上方向にスクロールしたら自動スクロールを停止する
 * - ユーザーが最下部まで戻したら自動スクロールを再開する
 */
export const TranscriptDisplay = () => {
	const transcriptionText = useAppStore((state) => state.transcriptionText);
	const interimText = useAppStore((state) => state.interimText);
	const t = useTranslation();

	const containerRef = useRef<HTMLDivElement>(null);
	const autoScrollEnabledRef = useRef(true);
	const lastScrollPositionRef = useRef(0);

	// テキストの変化に応じて最下部へ自動スクロール
	// biome-ignore lint/correctness/useExhaustiveDependencies: テキストの変化にのみ反応させる
	useEffect(() => {
		if (containerRef.current && autoScrollEnabledRef.current) {
			// 二重のrequestAnimationFrameで確実にレンダリング後にスクロール
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					if (containerRef.current) {
						const container = containerRef.current;
						// scrollHeightに余裕を持たせて確実に最下部へ
						container.scrollTop = container.scrollHeight + 100;
						lastScrollPositionRef.current = container.scrollTop;
					}
				});
			});
		}
	}, [transcriptionText, interimText]);

	// スクロール位置の監視
	const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
		const container = e.currentTarget;
		const { scrollTop, scrollHeight, clientHeight } = container;

		// ユーザーが手動でスクロールしたかどうかを判定
		const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 5;

		// 最下部から離れた場合は自動スクロールを無効化
		if (!isAtBottom && scrollTop < lastScrollPositionRef.current) {
			autoScrollEnabledRef.current = false;
		} else if (isAtBottom) {
			// 最下部に戻ったら自動スクロールを再開
			autoScrollEnabledRef.current = true;
		}

		lastScrollPositionRef.current = scrollTop;
	};

	return (
		<div
			ref={containerRef}
			onScroll={handleScroll}
			className={appStyles.transcriptContainer}
			data-testid="transcript-container"
		>
			{transcriptionText || interimText ? (
				<div className={appStyles.transcriptText}>
					{transcriptionText}
					<span className="text-gray-400">{interimText}</span>
				</div>
			) : (
				<div className={appStyles.transcriptEmpty}>{t.transcriptionPlaceholder}</div>
			)}
		</div>
	);
};

export default TranscriptDisplay;
